import { AgGridReact, Flex, Button, ModalV2, Grid, HelpIndicator, Text } from "@jsq/ds/core";
import { Icon, IconType } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import { RiskRating } from "~/graphql/supergraphTypes";
import { AGGridCellRenderParams } from "~/pages/Compliance/ComplianceTable/types";
import { RiskRatingRenderer } from "~/pages/Compliance/riskRating/RiskRatingRenderer";
import { DomicileRenderer } from "../DomicileRenderer";
import { InspectRiskRatingRowItem } from "../types";
import { lastRowSeparator, rightBorder } from "./styles_vanilla.css";

interface Props {
  showModal: boolean;
  onClose: () => void;
  riskRatingRowData: Array<{
    name: string;
    existing: {
      country: string | null;
      risk: RiskRating | null;
    };
    proposed: {
      country: string | null;
      risk: RiskRating | null;
      changeIndicator?: IconType;
    };
  }>;
  paymentBeneficiariesCount?: number;
  picrRevampEnabled: boolean;
  positionName: string;
}

const ExistingInstructionsHeader = (props: any) => {
  const paymentBeneficiariesCount = props.context?.paymentBeneficiariesCount || 0;
  return (
    <Flex alignItems="center" justifyContent="center" gap={4} style={{ width: "100%" }}>
      <Text>Existing payment instructions</Text>
      {paymentBeneficiariesCount > 1 && (
        <HelpIndicator
          id="multiple-pb-tooltip"
          placement="top"
          content="Multiple payment beneficiaries found"
        >
          <Icon type="info-circle" color={Colors.Warning600} />
        </HelpIndicator>
      )}
    </Flex>
  );
};

const InspectRiskRatingModal = ({
  showModal,
  onClose,
  riskRatingRowData,
  paymentBeneficiariesCount = 0,
  picrRevampEnabled = false,
  positionName,
}: Props) => {
  const riskRatingDefs: AgGridReact.AgGridReactProps["columnDefs"] = [
    {
      headerName: "",
      field: "name",
      minWidth: 250,
      flex: 1,
      cellClass: rightBorder,
    },
    {
      headerName: picrRevampEnabled ? "Current risk rating" : "Existing payment instructions",
      headerComponent: !picrRevampEnabled ? ExistingInstructionsHeader : undefined,
      field: "existing",
      minWidth: 220,
      flex: 1,
      cellClass: rightBorder,
      cellRenderer: (params: AGGridCellRenderParams) => {
        const data = params.data as InspectRiskRatingRowItem;
        if (data.name === "Bank account domicile" && !picrRevampEnabled) {
          return <DomicileRenderer params={{ value: data.existing.country || "" }} />;
        } else {
          return <RiskRatingRenderer riskRating={data.existing.risk} />;
        }
      },
    },
    {
      headerName: picrRevampEnabled ? "Proposed risk rating" : "Proposed payment instructions",
      field: "proposed",
      minWidth: 220,
      flex: 1,
      cellRenderer: (params: AGGridCellRenderParams) => {
        const data = params.data as InspectRiskRatingRowItem;
        if (data.name === "Bank account domicile" && !picrRevampEnabled) {
          return <DomicileRenderer params={{ value: data.proposed.country || "" }} />;
        } else {
          return (
            <RiskRatingRenderer
              riskRating={data.proposed.risk}
              changeIcon={data.proposed.changeIndicator ?? undefined}
            />
          );
        }
      },
    },
  ];

  const gridOptions: AgGridReact.AgGridReactProps["gridOptions"] = {
    autoSizeStrategy: {
      type: "fitGridWidth",
    },
    suppressCellFocus: true,
    rowHeight: 42,
    domLayout: "autoHeight",
    context: {
      paymentBeneficiariesCount,
    },
    getRowClass: (params) => {
      const isLastRow = params.node.rowIndex === params.api.getDisplayedRowCount() - 1;
      return isLastRow ? lastRowSeparator : "";
    },
  };

  return (
    <ModalV2
      show={showModal}
      onHide={onClose}
      size="large"
      title="Inspect risk rating"
      description={picrRevampEnabled ? positionName : ""}
    >
      <ModalV2.Body>
        <Flex justifyContent="center">
          <Grid
            gridOptions={gridOptions}
            rowData={riskRatingRowData}
            columnDefs={riskRatingDefs}
            suppressColumnVirtualisation={true}
            suppressRowVirtualisation={true}
          ></Grid>
        </Flex>
      </ModalV2.Body>
      <ModalV2.Footer>
        <Flex flexDirection="row" flexWrap="nowrap" justifyContent="flex-end">
          <Button variant="primary" onClick={onClose} data-cy="cancel-button">
            Close
          </Button>
        </Flex>
      </ModalV2.Footer>
    </ModalV2>
  );
};

export default InspectRiskRatingModal;
